import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Inbox,
  LayoutDashboard,
  Package,
  Server,
  Boxes,
  ShoppingCart,
  Users,
  GitBranch,
  Tag,
  Globe,
  Cpu,
  ShoppingBag,
  PanelsTopLeft,
  ArrowLeftRight,
  Activity,
  BellRing,
  FolderGit2,
  ChartNoAxesColumn,
  HardDrive,
  Layers,
  Network,
} from 'lucide-react';
import clsx from 'clsx';
import SidebarVersion from './SidebarVersion';
import { useAuth } from '../contexts/AuthContext';
import { mayAccess } from '../routeAccess';
import { useNetzName, VERBUND_PFAD } from '../lib/netz';

/**
 * The operational navigation — and only that.
 *
 * Platform administration (users, integrations, keys, audit) and personal
 * account settings used to sit in this same flat list, which gave "API-Keys"
 * and "Passwort ändern" the same visual weight as "Instanzen". Both moved to
 * the eyebrow bar; see Eyebrow.tsx.
 */

interface NavItem {
  to: string;
  /** Translation key, resolved at render time so it follows a language switch. */
  labelKey: string;
  /**
   * Statt des Übersetzungsschlüssels ein Name, der nicht übersetzt wird —
   * der des Verlagsverbunds kommt aus dem Backend (lib/netz.ts).
   */
  netzName?: boolean;
  /**
   * Nur zeigen, wer die Seite öffnen darf. Bisher stehen alle Einträge für
   * alle da und die Seite erklärt dann, wer hindarf; für den Verbund wäre
   * schon der Name ein Eintrag, mit dem die meisten nichts anfangen.
   */
  nurBerechtigt?: boolean;
  icon: React.ComponentType<{ className?: string; size?: string | number }>;
}

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

// Grouped along the actual workflow: what we sell, who buys it, what runs.
const navGroups: NavGroup[] = [
  {
    labelKey: 'nav.sectionOverview',
    items: [
      { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      // Denselben Inhalt gibt es als Reiter im Dashboard. Hier steht er
      // trotzdem: wer wissen will, ob die Server laufen, will einen Klick
      // dafuer und nicht zwei.
      { to: '/monitoring', labelKey: 'nav.monitoring', icon: Activity },
      // Direkt neben der Ueberwachung: wer einen Ausfall sieht, will von
      // dort aus wissen, wer geweckt wird -- und uebernehmen koennen.
      { to: '/bereitschaft', labelKey: 'nav.bereitschaft', icon: BellRing },
      // Neben Monitoring: dort geht es um die Wirte, hier um das, was
      // darauf laeuft und ob jemand es abruft.
      { to: '/analytics', labelKey: 'nav.analytics', icon: ChartNoAxesColumn },
    ],
  },
  {
    labelKey: 'nav.sectionBusiness',
    items: [
      { to: '/customers', labelKey: 'nav.customers', icon: Users },
      { to: '/orders', labelKey: 'nav.orders', icon: ShoppingCart },
      { to: '/products', labelKey: 'nav.products', icon: Package },
      // Was ausgerollt wird, neben dem, was verkauft wird. Siehe
      // docs/KONZEPT-PRODUKT-UND-APPVORLAGE.md.
      { to: '/applications', labelKey: 'nav.applications', icon: Layers },
    ],
  },
  {
    labelKey: 'nav.sectionOperations',
    items: [
      { to: '/instances', labelKey: 'nav.instances', icon: Boxes },
      // Drei Reiter, eine Tabelle: die Sichten filtern nach `art`. Siehe
      // docs/KONZEPT-APPS-UND-WEBSEITEN.md.
      { to: '/apps', labelKey: 'nav.apps', icon: ShoppingBag },
      { to: '/webseiten', labelKey: 'nav.webseiten', icon: PanelsTopLeft },
      { to: '/fm-migration', labelKey: 'nav.fmMigration', icon: ArrowLeftRight },
      { to: '/servers', labelKey: 'nav.servers', icon: Server },
      // Neben den Servern: ein Sicherungsziel ist Infrastruktur, kein
      // Geschaeftsvorgang — und wer es sucht, sucht es dort.
      { to: '/sicherungsziele', labelKey: 'nav.sicherungsziele', icon: HardDrive },
      { to: '/domains', labelKey: 'nav.domains', icon: Globe },
      { to: '/inference', labelKey: 'nav.inference', icon: Cpu },
      // Verlagsverbund: Anträge, Verlage, Sperren. Neben dem KI-Gateway —
      // beides ist ein eigener Dienst, den der PC verwaltet, nicht betreibt.
      { to: VERBUND_PFAD, labelKey: 'nav.verbund', icon: Network, netzName: true, nurBerechtigt: true },
      { to: '/deployments', labelKey: 'nav.deployments', icon: GitBranch },
      // Quelltext und Abzuege haengen an derselben Maschine, und beides ist
      // Betrieb: wer hier ist, will wissen, wer Zugang hat oder warum die
      // Platte voll ist.
      { to: '/git-registry', labelKey: 'nav.gitRegistry', icon: FolderGit2 },
      { to: '/releases', labelKey: 'nav.releases', icon: Tag },
      // Unter Betrieb und nicht unter Geschaeft: was hier ankommt, ist der
      // Ruecklauf aus laufenden Installationen — eine Betriebsfrage, keine
      // kaufmaennische.
      { to: '/feedback', labelKey: 'nav.feedback', icon: Inbox },
    ],
  },
];

export default function Sidebar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const netzName = useNetzName();

  return (
    <aside
      // Absichtlich dunkel, in beiden Fassungen. Als Name statt als Hexwert,
      // damit die Farbe dort steht, wo die anderen auch stehen.
      className="flex w-60 min-h-screen shrink-0 flex-col bg-navigation"
    >
      {/* Logo */}
      <div className="border-b border-slate-700/50 px-5 py-5">
        <img src="/pc-logo-white.svg" alt={t('nav.subtitle')} className="h-auto w-32" />
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-5 px-3 py-4">
        {navGroups.map((group) => (
          <div key={group.labelKey}>
            <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {t(group.labelKey)}
            </div>
            <div className="space-y-0.5">
              {group.items
                .filter((item) => !item.nurBerechtigt || mayAccess(item.to, user?.role))
                .map(({ to, labelKey, icon: Icon, netzName: alsNetzName }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-brand-600 text-white'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-white',
                    )
                  }
                >
                  <Icon size={16} className="shrink-0" />
                  {alsNetzName ? netzName : t(labelKey)}
                </NavLink>
                ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Welche Fassung laeuft. Unten, weil es dort niemanden stoert und
          trotzdem jeder findet, der danach sucht. */}
      <SidebarVersion />
    </aside>
  );
}
